import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SectionDivider from "@/components/SectionDivider";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ClipboardEdit,
  MapPin,
  QrCode,
  Hourglass,
  Truck,
  Phone,
  Weight,
  User,
  Building2,
  Gauge,
  Clock,
  Euro,
  Check,
  PlayCircle,
  X,
} from "lucide-react";

const benefits = [
  {
    icon: Gauge,
    label: "Bedarfsgerecht",
    title: "Bedarfsgerechte Menge",
    desc: "Ab 0,15 m³ – nehmen Sie nur so viel Beton, wie Sie wirklich brauchen.",
    points: ["Keine Verschwendung", "Keine Restmengen", "Schon ab 0,15 m³"],
  },
  {
    icon: Clock,
    label: "Flexibel",
    title: "Zeitersparnis & Flexibilität",
    desc: "Kein langes Warten wie bei herkömmlichen Betonwerken. Kommen Sie vorbei, wann es Ihnen passt – auch am Samstag.",
    points: [
      "Keine Wartezeit hinter Mischfahrzeugen",
      "Kein eigenes Mischen mit Kies & Zement",
      "Auch samstags geöffnet",
    ],
  },
  {
    icon: Euro,
    label: "Transparent",
    title: "Kostentransparent",
    desc: "Klare Preise pro m³. Einfach laden – keine Extrakosten.",
    points: [
      "Sie zahlen nur, was Sie laden",
      "Keine versteckten Gebühren",
      "Staffelrabatte für Gewerbekunden",
    ],
  },
];

const steps = [
  {
    num: "01",
    icon: ClipboardEdit,
    title: "Bestellen",
    desc: "Produkthinweise beachten und am digitalen Terminal die gewünschte Betonsorte sowie Menge auswählen.",
  },
  {
    num: "02",
    icon: MapPin,
    title: "Platzieren",
    desc: "Transportmittel mittig unter dem Förderband positionieren – sicher und gerade ausgerichtet.",
  },
  {
    num: "03",
    icon: QrCode,
    title: "Scannen",
    desc: "QR-Code am Scanner beim Förderband einscannen, um die Ausgabe freizuschalten.",
  },
  {
    num: "04",
    icon: Hourglass,
    title: "Bitte warten",
    desc: "Produktion läuft – ca. 0,50 m³ Frischbeton in nur 3 Minuten direkt vor Ort gemischt.",
  },
  {
    num: "05",
    icon: Truck,
    title: "Mitnehmen",
    desc: "Beton verladen und losfahren. Die Ladungssicherung liegt in der Verantwortung des Fahrers.",
  },
];

const transport = [
  { volume: "0,15 m³", weight: "330 kg" },
  { volume: "0,25 m³", weight: "550 kg" },
  { volume: "1,0 m³", weight: "2200 kg" },
];

const tutorialVideoSrc = "/videos/beton2go-anleitung.mp4?v=20260430";

const Funktion = () => {
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    if (!videoOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setVideoOpen(false);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [videoOpen]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(210_100%_15%)_0%,transparent_60%)] opacity-60" />
          <div className="container mx-auto px-4 text-center relative">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <span className="text-primary font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
                Bedienungsanleitung
              </span>
              <h1 className="font-display text-5xl md:text-6xl font-bold mb-4">
                So <span className="text-primary">funktioniert's</span>
              </h1>
              <p className="text-muted-foreground max-w-xl mx-auto">
                In 5 einfachen Schritten zu Ihrem Frischbeton – rund um die Uhr in Selbstbedienung.
              </p>
            </motion.div>
          </div>
        </section>

      {/* Erklär-Video */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-xl mx-auto text-center"
          >
            <span className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-3 block">
              Video-Anleitung
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
              Sehen Sie den <span className="text-primary">Ablauf</span>
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              Der gesamte Prozess kompakt erklärt – von der Anfahrt bis zur Abfahrt.
            </p>
            <button
              onClick={() => setVideoOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 font-display text-sm uppercase tracking-[0.15em] font-bold hover:bg-primary/80 transition-colors"
            >
              <PlayCircle className="h-5 w-5" />
              Video abspielen
            </button>
          </motion.div>
        </div>
      </section>

      {/* Video Dialog */}
      {videoOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tutorial-video-title"
          aria-describedby="tutorial-video-description"
          onClick={() => setVideoOpen(false)}
        >
          <div
            className="relative overflow-hidden border border-primary/40 bg-black shadow-2xl"
            style={{ width: "min(92vw, 45vh, 480px)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="tutorial-video-title" className="sr-only">
              Beton2Go Erklär-Video
            </h2>
            <p id="tutorial-video-description" className="sr-only">
              Video-Anleitung zum Ablauf an der Beton2Go Betontankstelle.
            </p>
            <video
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="block h-auto max-h-[88vh] w-full bg-black object-contain"
            >
              <source src={tutorialVideoSrc} type="video/mp4" />
              Ihr Browser unterstützt das Video-Tag nicht.
            </video>
            <button
              type="button"
              aria-label="Video schließen"
              onClick={() => setVideoOpen(false)}
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white backdrop-blur-sm transition-colors hover:bg-black/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-black"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* 5 Steps */}
      <section className="py-16 bg-secondary/30 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `repeating-linear-gradient(45deg, hsl(var(--primary)), hsl(var(--primary)) 1px, transparent 1px, transparent 40px)`,
          }}
        />
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {steps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="relative bg-card border border-border group hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_hsl(210_100%_50%/0.15)] flex flex-col"
              >
                {/* Number bar */}
                <div className="bg-primary text-primary-foreground py-3 px-4 flex items-center justify-center">
                  <span className="font-display text-3xl font-bold">{step.num.replace("0", "")}</span>
                </div>

                {/* Icon */}
                <div className="p-6 flex flex-col items-center text-center flex-1">
                  <div className="w-20 h-20 bg-primary flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                    <step.icon className="h-10 w-10 text-primary-foreground" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4 flex-1">
                    {step.desc}
                  </p>
                  <div className="border-t border-border pt-3 w-full mt-auto">
                    <h3 className="font-display text-xl font-bold tracking-wider text-primary uppercase">
                      {step.title}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Benefits */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-primary font-sans text-sm tracking-[0.3em] uppercase mb-4 block">
              Ihre Vorteile
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Warum <span className="text-primary">Beton2Go</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Drei klare Gründe für die Beton-Tankstelle in Nalbach.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="bg-card border border-border p-8 hover:border-primary/50 transition-all duration-300 group relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 blur-2xl group-hover:bg-primary/10 transition-colors" />
                <span className="text-primary/60 font-mono text-[10px] tracking-[0.3em] uppercase block mb-4">
                  0{i + 1} / {b.label}
                </span>
                <div className="w-14 h-14 bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                  <b.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {b.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">{b.desc}</p>
                <ul className="space-y-2 border-t border-border pt-4">
                  {b.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Slogan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mt-20 text-center border-y border-primary/20 py-12 relative"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(210_100%_15%)_0%,transparent_70%)] opacity-40" />
            <p className="relative font-display text-2xl md:text-3xl font-bold leading-tight">
              Während andere im Betonwerk{" "}
              <span className="text-muted-foreground/70">warten</span>,{" "}
              <span className="text-primary">arbeiten Sie</span> bei uns{" "}
              <span className="text-primary">schon.</span>
            </p>
          </motion.div>
        </div>
      </section>

      <SectionDivider />

      {/* Transport-Info & Notfall */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Transport-Information (spans 2 cols) */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="md:col-span-2 bg-primary text-primary-foreground p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Weight className="h-6 w-6" />
                <h2 className="font-display text-2xl font-bold tracking-wider uppercase">
                  Transport-Information
                </h2>
              </div>
              <p className="text-sm mb-6 opacity-90">
                Achten Sie auf das zulässige Gesamtgewicht Ihres Transportmittels:
              </p>
              <div className="grid grid-cols-3 gap-4">
                {transport.map((t) => (
                  <div
                    key={t.volume}
                    className="bg-primary-foreground/10 border border-primary-foreground/20 p-4 text-center"
                  >
                    <div className="font-display text-2xl font-bold mb-1">{t.volume}</div>
                    <div className="text-xs uppercase tracking-wider opacity-80">{t.weight}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Kontakt */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-card border border-border p-8 flex flex-col justify-center text-center"
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <Phone className="h-5 w-5 text-primary" />
                <h2 className="font-display text-2xl font-bold tracking-wider uppercase text-primary">
                  Kontakt
                </h2>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
                Rückfragen vor Ort
              </p>
              <a
                href="tel:+4915141315272"
                className="font-display text-lg md:text-xl font-bold hover:text-primary transition-colors"
              >
                0151 / 41 31 52 72
              </a>
              <p className="text-sm text-muted-foreground mt-3">
                Für organisatorische Fragen zur Abholung und Nutzung der Station.
              </p>
            </motion.div>
          </div>

          {/* Privat- vs Gewerbekunden */}
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card border border-border p-6 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-display text-lg font-bold tracking-wider uppercase text-primary">
                  Privatkunden
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Jeder Privatkunde kann sich ohne Registrierung Frischbeton holen und per EC-Karte
                am digitalen Terminal zahlen.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border p-6 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-display text-lg font-bold tracking-wider uppercase text-primary">
                  Gewerbekunden
                </h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Als Gewerbekunde empfiehlt sich eine Anmeldung bei Beton2Go Romano vor der ersten
                Nutzung, um vorab als Rechnungskunde registriert zu werden.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
            Bereit für Ihren <span className="text-primary">Beton</span>?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Sehen Sie sich die Preise an oder besuchen Sie uns direkt in Nalbach.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#preise"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 font-display text-sm uppercase tracking-[0.15em] font-bold hover:bg-primary/80 transition-colors"
            >
              Preise ansehen <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/#standort"
              className="inline-flex items-center justify-center px-6 py-3 font-display text-sm uppercase tracking-[0.15em] border border-border text-muted-foreground hover:bg-muted/30 hover:text-foreground transition-colors"
            >
              Standort anzeigen
            </Link>
          </div>
        </div>
      </section>

      </main>

      <Footer />
    </div>
  );
};

export default Funktion;
